import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Trash2, Loader2, Globe, X, Link as LinkIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { BuyerReport } from "@/hooks/useBuyerReportData";

interface BuyerReportCardProps {
  report: BuyerReport;
  onUpdate?: () => void;
  hoverColor?: string;
}

export function BuyerReportCard({ report, onUpdate, hoverColor = "#e8edf5" }: BuyerReportCardProps) {
  const { toast } = useToast();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const fullAddress = [report.address, report.city, report.state, report.zip]
    .filter(Boolean)
    .join(", ");

  const offerRange =
    report.suggested_offer_low && report.suggested_offer_high
      ? `$${(report.suggested_offer_low / 1000).toFixed(0)}K–$${(report.suggested_offer_high / 1000).toFixed(0)}K`
      : null;

  const agentName = report.custom_agent_name;
  const agentPhoto = report.custom_agent_headshot_url;
  const agentInitials = agentName
    ? agentName.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "";

  const listPrice = report.property_data?.price || report.property_data?.zestimate;
  const imageUrl =
    report.property_data?.desktopWebHdpImageLink ||
    report.property_data?.hiResImageLink ||
    report.property_data?.photos?.[0]?.mixedSources?.jpeg?.[0]?.url ||
    report.property_data?.photos?.[0]?.url ||
    report.property_data?.imgSrc;

  const status = report.is_published ? "published" : "draft";
  const hasAnalysis = !!report.ai_analysis_structured;

  const handlePublishToggle = async () => {
    setIsPublishing(true);
    try {
      const { error } = await supabase
        .from("property_reports")
        .update({
          is_published: !report.is_published,
          published_at: !report.is_published ? new Date().toISOString() : null,
        })
        .eq("id", report.id);
      if (error) throw error;
      toast({ title: report.is_published ? "Report Unpublished" : "Report Published" });
      onUpdate?.();
    } catch {
      toast({ title: "Error", description: "Failed to update publish status", variant: "destructive" });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!report.is_published) {
      toast({ title: "Not published", description: "Publish the report first", variant: "destructive" });
      return;
    }
    const url = `${window.location.origin}/buyer-report/public/${report.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied!" });
    } catch {
      toast({ title: "Error", description: "Failed to copy link", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText.toLowerCase() !== "delete") {
      toast({ title: "Error", description: "Type 'delete' to confirm", variant: "destructive" });
      return;
    }
    setIsDeleting(true);
    try {
      const { error } = await supabase.from("property_reports").delete().eq("id", report.id);
      if (error) throw error;
      toast({ title: "Report deleted" });
      onUpdate?.();
    } catch {
      toast({ title: "Error", description: "Failed to delete report", variant: "destructive" });
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
      setDeleteConfirmText("");
    }
  };

  const createdDate = new Date(report.created_at).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      <AlertDialog
        open={isDeleteOpen}
        onOpenChange={(open) => {
          setIsDeleteOpen(open);
          if (!open) setDeleteConfirmText("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Buyer Report?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this Buyer Report. This action cannot be undone.
              <div className="mt-4">
                <Label htmlFor="prCardDeleteConfirm">Type "delete" to confirm:</Label>
                <Input
                  id="prCardDeleteConfirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="delete"
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting || deleteConfirmText.toLowerCase() !== "delete"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="bg-card border border-border rounded-xl overflow-hidden flex flex-col group hover:shadow-md transition-shadow duration-200">
        {/* Header */}
        {imageUrl ? (
          <div className="relative aspect-[16/9] overflow-hidden bg-muted flex-shrink-0">
            <img
              src={imageUrl}
              alt={fullAddress}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
            />
            <div className="absolute top-2.5 left-2.5">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase bg-black/55 text-white px-2 py-1 rounded-md backdrop-blur-sm">
                Buyer Report
              </span>
            </div>
            {listPrice && (
              <div className="absolute bottom-2.5 left-2.5 bg-black/55 text-white text-sm font-bold px-2.5 py-1 rounded-md backdrop-blur-sm">
                ${(listPrice / 1000).toFixed(0)}K
              </div>
            )}
          </div>
        ) : (
          <div className="relative h-28 bg-gradient-to-br from-emerald-500/15 via-teal-500/10 to-cyan-500/10 flex items-end p-3.5 flex-shrink-0">
            <div className="absolute top-2.5 left-2.5">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase bg-teal-700/75 text-white px-2 py-1 rounded-md backdrop-blur-sm">
                Buyer Report
              </span>
            </div>
            {listPrice && (
              <p className="text-xl font-bold text-foreground/80 leading-none">
                ${(listPrice / 1000).toFixed(0)}K
              </p>
            )}
          </div>
        )}

        {/* Card body */}
        <div className="p-4 flex-1 flex flex-col gap-1.5 min-h-0">
          <Link
            to={`/buyer-report/${report.id}`}
            className="font-semibold text-sm text-foreground hover:text-primary hover:underline leading-snug line-clamp-2"
          >
            {fullAddress}
          </Link>

          <p className="text-xs text-muted-foreground truncate">
            {report.buyer_name ? `For: ${report.buyer_name}` : <span className="invisible">—</span>}
          </p>

          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {status === "published" ? (
              <Badge variant="default" className="gap-1 bg-green-600 text-white text-xs">
                <Globe className="h-3 w-3" />
                Published
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-xs">
                Draft
              </Badge>
            )}
            {hasAnalysis && (
              <Badge variant="outline" className="gap-1 text-xs text-emerald-600 border-emerald-200">
                AI Analysis
              </Badge>
            )}
            {offerRange && (
              <Badge variant="outline" className="gap-1 text-xs text-blue-600 border-blue-200">
                {offerRange}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-1.5 mt-auto pt-1.5">
            {agentPhoto ? (
              <img src={agentPhoto} alt={agentName || ""} className="h-5 w-5 rounded-full object-cover flex-shrink-0" />
            ) : agentInitials ? (
              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <span className="text-[9px] font-semibold text-muted-foreground">{agentInitials}</span>
              </div>
            ) : null}
            {agentName && (
              <span className="text-xs text-muted-foreground truncate flex-1">{agentName}</span>
            )}
            <span className="text-xs text-muted-foreground/70 flex-shrink-0 ml-auto">{createdDate}</span>
          </div>
        </div>

        {/* Action footer */}
        <div className="px-3 py-2 border-t border-border flex items-center gap-0.5">
          <Link
            to={`/buyer-report/${report.id}`}
            title="View Report"
            className="inline-flex items-center gap-1.5 flex-1 justify-center h-8 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverColor; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
          >
            <Eye className="h-3.5 w-3.5" />
            View
          </Link>
          <div className="w-px h-4 bg-border mx-0.5" />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={handlePublishToggle}
            disabled={isPublishing}
            title={report.is_published ? "Unpublish" : "Publish"}
          >
            {isPublishing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : report.is_published ? (
              <Globe className="h-3.5 w-3.5 text-green-600" />
            ) : (
              <div className="relative inline-flex items-center justify-center h-3.5 w-3.5">
                <Globe className="h-3.5 w-3.5" />
                <X className="h-3.5 w-3.5 absolute rotate-45" />
              </div>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={handleCopyLink}
            disabled={!report.is_published}
            title={report.is_published ? "Copy link" : "Not published"}
          >
            <LinkIcon className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsDeleteOpen(true)}
            title="Delete Report"
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      </div>
    </>
  );
}
